import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowRight, ChevronRight, MapPin, CheckCircle2, Mail, Loader2, Target, CalendarDays } from "lucide-react";
import AdBanner from "@/components/AdBanner";
import { EventCard } from "@/components/EventCard";
import {
  CATEGORY_LINKS, CategoryIcon, ShieldCheckIcon, StoreIcon, ScaleIcon,
  ScopeIcon, HuntingIcon, PartsIcon, ArcheryIcon,
} from "@/components/icons/GunIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category tiles share their labels/paths with the header nav and footer
// (single source of truth in GunIcons). The keys map to backend categories.
const CATEGORY_TILES = CATEGORY_LINKS.map((c) => (
  c.key === "ammo" ? { ...c, label: "Ammunition" } :
  c.key === "firearm_storage" ? { ...c, label: "Firearm Storage" } : c
));

function daysAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "1 day ago";
  return `${diff} days ago`;
}

function conditionColor(c) {
  const map = { "new": "bg-emerald-50 text-emerald-800", "excellent": "bg-emerald-50 text-emerald-700", "very_good": "bg-slate-100 text-slate-700", "good": "bg-slate-100 text-slate-700", "fair": "bg-amber-50 text-amber-700", "parts_only": "bg-red-50 text-red-700" };
  return map[c] || "bg-slate-100 text-slate-700";
}

function conditionLabel(c) {
  return (c || "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

/** Section heading: title left, optional link right. Replaces the old orange left-border h2. */
export function SectionHeading({ title, to, linkLabel, testId, as: Tag = "h2" }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <Tag className="font-heading text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight leading-none">{title}</Tag>
      {to && (
        <Link to={to} className="text-sm font-medium text-slate-600 hover:text-orange-600 inline-flex items-center gap-1.5 shrink-0" data-testid={testId}>
          {linkLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export function ListingCard({ listing }) {
  const photoUrl = listing.photos?.length > 0
    ? `${API}/files/${listing.photos[0]}`
    : "/listing-placeholder.png";
  const isProperty = listing.category === "hunting_services";
  const title = isProperty
    ? (listing.property_name || listing.title || `${listing.make} ${listing.model}`)
    : `${listing.make} ${listing.model}`;

  return (
    <Link
      to={`/listing/${listing.slug}`}
      className="listing-card bg-white border border-slate-200 rounded-sm overflow-hidden group flex flex-col transition-colors"
      data-testid={`listing-card-${listing.listing_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img src={photoUrl} alt={title} className="w-full h-full object-contain" loading="lazy" width="400" height="300" decoding="async" />
        {listing.is_featured && (
          <span className="absolute top-2.5 left-2.5 bg-orange-600 text-white text-[11px] font-bold tracking-[0.06em] px-2 py-0.5 rounded-sm">FEATURED</span>
        )}
        {listing.status === "sold" && (
          <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
            <span className="text-white font-heading text-2xl font-bold tracking-tight">SOLD</span>
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="listing-card-title font-heading text-[17px] font-bold text-slate-900 leading-tight line-clamp-2 transition-colors">
          {title}
        </h3>
        <p className="text-[13px] text-slate-500 flex items-center gap-1.5 flex-wrap">
          {isProperty ? (
            <span>{listing.region ? `${listing.region}, ${listing.state}` : listing.state}</span>
          ) : listing.calibre ? (
            <span>{listing.calibre}</span>
          ) : null}
          {!isProperty && listing.condition && (
            <>
              {listing.calibre && <span aria-hidden="true">·</span>}
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-sm ${conditionColor(listing.condition)}`}>{conditionLabel(listing.condition)}</span>
            </>
          )}
          {isProperty && listing.game_available?.length > 0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">{listing.game_available.slice(0, 2).join(", ")}{listing.game_available.length > 2 ? ` +${listing.game_available.length - 2}` : ""}</span>
          )}
        </p>
        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="font-heading text-xl font-bold text-emerald-700 num">
            ${listing.price?.toLocaleString()}
            {listing.price_unit && <span className="text-xs font-normal text-slate-400 ml-1 font-body">{listing.price_unit}</span>}
          </span>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <MapPin className="h-3.5 w-3.5 text-slate-400" />{listing.state}
          </span>
        </div>
        <div className="border-t border-slate-200 pt-2.5 flex items-center justify-between gap-2">
          {listing.seller_type === "dealer" ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700" data-testid="badge-dealer">
              <StoreIcon className="h-3.5 w-3.5" /> Verified dealer
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="badge-licensed">
              <ShieldCheckIcon className="h-3.5 w-3.5" /> Licensed seller
            </span>
          )}
          <span className="text-xs text-slate-400">{daysAgo(listing.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [latestListings, setLatestListings] = useState([]);
  const [soldListings, setSoldListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingsRes, catsRes, soldRes] = await Promise.all([
          axios.get(`${API}/listings/latest?limit=12`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/listings/recently-sold?limit=8`),
        ]);
        setLatestListings(listingsRes.data);
        setCategories(catsRes.data);
        setSoldListings(soldRes.data);
      } catch (err) {
        console.error("Failed to fetch homepage data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    axios.get(`${API}/stats/homepage`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Page meta (WebSite + Organization JSON-LD live in static index.html)
  useEffect(() => {
    document.title = "gunbuy.com.au - Australia's Firearms Classifieds | Buy & Sell Used Firearms";
    const desc = "Australia's trusted classifieds for buying and selling used firearms, accessories and services. Browse rifles, shotguns, handguns and more from licensed sellers and dealers.";
    const metas = [
      { sel: 'meta[name="description"]', val: desc },
      { sel: 'meta[property="og:title"]', val: "gunbuy.com.au - Australia's Firearms Classifieds" },
      { sel: 'meta[property="og:description"]', val: desc },
    ];
    metas.forEach(({ sel, val }) => {
      const tag = document.querySelector(sel);
      if (tag) tag.setAttribute("content", val);
    });
    return () => { document.title = "gunbuy.com.au - Australia's Firearms Classifieds"; };
  }, []);

  const steps = [
    { Icon: ScaleIcon, title: "Licence checked", desc: "Sellers verify their firearms licence before a listing goes live." },
    { Icon: Mail, title: "Enquire in app", desc: "Message the seller and agree a price without sharing your details." },
    { Icon: StoreIcon, title: "Transfer at a dealer", desc: "Nominate a licensed dealer to complete the transfer legally." },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero — search first */}
      <section className="bg-white border-b border-slate-200" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end">
          <div className="lg:col-span-7 flex flex-col gap-5">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700">
              <ShieldCheckIcon className="h-4 w-4" /> Every transfer through a licensed dealer
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.02] text-pretty">
              Find &amp; list used firearms in Australia
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              Australia's trusted classifieds for licensed buyers, sellers and dealers. Buy and sell rifles, shotguns, optics and accessories in every state.
            </p>
            <form onSubmit={handleSearch} className="flex w-full max-w-2xl border-[1.5px] border-slate-900 rounded-sm bg-white overflow-hidden" role="search">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 pointer-events-none" aria-hidden="true" />
                <Input
                  data-testid="hero-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by make, model or calibre"
                  aria-label="Search by make, model or calibre"
                  className="pl-12 h-14 text-base border-0 rounded-none bg-transparent placeholder:text-slate-500 focus-visible:ring-0 focus-visible:border-0"
                />
              </div>
              <Button type="submit" className="h-14 px-6 sm:px-8 rounded-none text-[15px]" data-testid="hero-search-btn">
                Search
              </Button>
            </form>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-500 num" data-testid="live-inventory-strip">
              {stats && stats.active_listings > 0 && (
                <span data-testid="live-listings-count"><b className="text-slate-900 font-semibold">{stats.active_listings.toLocaleString()}</b> live listings</span>
              )}
              {stats && stats.dealers_with_inventory > 0 && (
                <span data-testid="live-dealers-count"><b className="text-slate-900 font-semibold">{stats.dealers_with_inventory.toLocaleString()}</b> Australian dealer{stats.dealers_with_inventory !== 1 ? "s" : ""} with stock listed</span>
              )}
              <span><b className="text-slate-900 font-semibold">8</b> states &amp; territories</span>
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            {steps.map((s, i) => (
              <div key={s.title} className="flex items-start gap-4 p-4 sm:p-5 bg-slate-50 border border-slate-200 rounded-sm">
                <div className="w-10 h-10 shrink-0 rounded-sm bg-white border border-slate-300 flex items-center justify-center text-slate-900">
                  <s.Icon className="h-5 w-5" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">Step {i + 1}</span>
                  <h3 className="font-heading text-lg font-bold text-slate-900 leading-tight">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-snug">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Homepage Hero Ad */}
      <AdBanner placement="homepage_hero" className="my-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" />

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4" data-testid="categories-section">
        <SectionHeading title="Browse by category" to="/search" linkLabel="All categories" testId="all-categories-link" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {CATEGORY_TILES.map((cat) => {
            const catData = categories.find(c => c.key === cat.key);
            return (
              <Link
                key={cat.key}
                to={cat.path}
                className="category-tile bg-white border border-slate-200 rounded-sm p-4 flex flex-col gap-4 min-h-[112px] transition-colors"
                data-testid={`category-tile-${cat.key}`}
              >
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center text-slate-900">
                  <CategoryIcon category={cat.key} className="h-[22px] w-[22px]" />
                </div>
                <div className="mt-auto">
                  <h3 className="category-tile-label text-sm font-semibold text-slate-900 leading-tight transition-colors">{cat.label}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 num">{catData?.count || 0} listings</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Latest Listings */}
      {!loading && latestListings.length === 0 ? null : (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14" data-testid="latest-listings-section">
          <SectionHeading title="Latest listings" to="/search" linkLabel={stats?.active_listings ? `View all ${stats.active_listings.toLocaleString()}` : "View all"} testId="view-all-link" />
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-sm overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-slate-100" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-slate-100 rounded-sm w-3/4" />
                    <div className="h-3 bg-slate-100 rounded-sm w-1/2" />
                    <div className="h-5 bg-slate-100 rounded-sm w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {latestListings.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Content Block 1: List Your Firearm the Right Way ── */}
      <section className="bg-slate-900 text-white" data-testid="content-sell-legally">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400 mb-4">
              <ScaleIcon className="h-4 w-4" /> Legal &amp; compliant
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight leading-[1.1] mb-4">
              List your firearm the right way
            </h2>
            <p className="text-slate-300 leading-relaxed mb-7 max-w-2xl">
              Every listing on GunBuy requires a nominated transfer agent — a licensed firearms dealer who handles the
              legal transfer, as required by Australian law. You nominate your dealer, the buyer nominates theirs,
              and everything is handled safely and compliantly — no grey areas, no risk.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild variant="accent" size="lg">
                <Link to="/register" data-testid="sell-legally-cta">Start listing</Link>
              </Button>
              <Link to="/terms" className="text-sm font-medium text-slate-300 hover:text-white inline-flex items-center gap-1 h-11 px-2">
                Learn how it works <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-3 gap-3">
            {[
              { n: stats?.active_listings ? stats.active_listings.toLocaleString() : "—", l: "live listings" },
              { n: "8", l: "states covered" },
              { n: "100%", l: "dealer transfers" },
            ].map((x) => (
              <div key={x.l} className="border border-slate-700 rounded-sm p-4 sm:p-5">
                <p className="font-heading text-3xl sm:text-4xl font-extrabold leading-none num">{x.n}</p>
                <p className="text-xs sm:text-[13px] text-slate-400 mt-1.5">{x.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Content Block 2: Verified Sellers Only ── */}
      <section className="bg-white border-b border-slate-200" data-testid="content-verified-sellers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700 mb-4">
              <ShieldCheckIcon className="h-4 w-4" /> Verified &amp; trusted
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-4">
              Every seller is licence verified
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              Whether you're a private seller or a licensed dealer, every account on GunBuy is manually verified by our team.
              We check your firearms licence before you can list a single item — so buyers know they're dealing with
              legitimate, licensed Australians.
            </p>
            <ul className="space-y-2.5 mb-7">
              {[
                "Private sellers verified by licence number",
                "Dealers verified by ABN and dealer licence",
                "Buyer enquiries from licensed individuals only",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <Button asChild variant="outline" size="lg">
              <Link to="/register" data-testid="get-verified-cta">Get verified &amp; list</Link>
            </Button>
          </div>
          <div className="lg:col-span-6 border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
            <img
              src="https://images.unsplash.com/photo-1564846824194-346b7871b855?w=800&h=600&fit=crop"
              alt="Verification process"
              className="w-full h-full object-cover aspect-[4/3]"
              loading="lazy" width="800" height="600" decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ── Content Block 3: Australia's Best Firearms Classifieds ── */}
      <section className="bg-slate-50 border-b border-slate-200" data-testid="content-best-classifieds">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 mb-4">
              <StoreIcon className="h-4 w-4" /> Dealers &amp; private sellers
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-4">
              Find your next firearm. Buy &amp; sell with confidence.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              From private sellers listing their first rifle to established dealers with hundreds of firearms in stock —
              GunBuy brings Australia's firearms community together in one trusted place. Browse dealer inventories,
              compare prices, and find exactly what you're looking for.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { label: "Licensed dealers", desc: "Browse verified dealer listings and visit their full inventory", Icon: StoreIcon },
              { label: "Private sellers", desc: "Browse listings from licensed individuals across every state", Icon: ShieldCheckIcon },
              { label: "National reach", desc: "Listings from NSW, VIC, QLD, SA, WA, TAS, NT and ACT", Icon: MapPin },
            ].map((item) => (
              <div key={item.label} className="bg-white border border-slate-200 rounded-sm p-5 flex gap-4">
                <div className="w-10 h-10 shrink-0 rounded-sm bg-slate-100 flex items-center justify-center text-slate-900">
                  <item.Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 leading-tight mb-1">{item.label}</h3>
                  <p className="text-sm text-slate-600 leading-snug">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg">
              <Link to="/search" data-testid="browse-all-cta">Browse all listings</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/dealers" data-testid="explore-dealers-cta">Explore dealer directory</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Content Block 4: More Than Just Firearms ── */}
      <section className="bg-white" data-testid="content-beyond-firearms">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <div className="max-w-2xl mb-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-3">
              More than just firearms
            </h2>
            <p className="text-slate-600 leading-relaxed">
              GunBuy is your one-stop classifieds for everything shooting, hunting and outdoors.
              From optics and ammunition to guided hunts and archery gear.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Scopes & Optics", desc: "Red dots, rifle scopes, spotting scopes and night vision from top brands.", path: "/scopes-optics", Icon: ScopeIcon },
              { title: "Hunting Properties", desc: "Private hunting land access across Australia. Book property for licensed hunting.", path: "/hunting-services", Icon: HuntingIcon },
              { title: "Parts & Accessories", desc: "Stocks, barrels, triggers, magazines, cases and everything in between.", path: "/parts-accessories", Icon: PartsIcon },
              { title: "Paintball & Archery", desc: "Compound bows, crossbows, paintball markers and all the gear you need.", path: "/paintball-archery", Icon: ArcheryIcon },
            ].map((item) => (
              <Link
                key={item.title}
                to={item.path}
                className="group bg-white border border-slate-200 rounded-sm p-5 flex flex-col gap-4 hover:border-slate-400 transition-colors"
                data-testid={`explore-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-sm bg-slate-100 flex items-center justify-center text-slate-900">
                  <item.Icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-1 group-hover:text-orange-600 transition-colors">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
                <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-slate-700 group-hover:text-orange-600">Browse <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recently Sold */}
      {soldListings.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200 py-12" data-testid="recently-sold-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <h2 className="font-heading text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight leading-none">Recently sold</h2>
                <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 num">
                  {soldListings.length} completed
                </Badge>
              </div>
              <Link to="/search?status=sold" className="text-sm font-medium text-slate-600 hover:text-orange-600 inline-flex items-center gap-1.5 shrink-0" data-testid="view-all-sold-link">
                View all sold <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {soldListings.map((listing) => {
                const photoUrl = listing.photos?.[0] ? `${API}/files/${listing.photos[0]}` : "/listing-placeholder.png";
                const timeAgo = daysAgo(listing.sold_at || listing.updated_at);
                return (
                  <Link
                    key={listing.listing_id}
                    to={`/listing/${listing.slug}`}
                    className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-slate-400 transition-colors"
                    data-testid={`sold-card-${listing.listing_id}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                      <img
                        src={photoUrl}
                        alt={`${listing.make} ${listing.model}`}
                        className="w-full h-full object-contain grayscale-[40%] group-hover:grayscale-0 transition-all duration-300"
                        loading="lazy" width="400" height="300" decoding="async"
                      />
                      <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-emerald-700 text-white text-[11px] font-bold tracking-[0.06em] px-2 py-0.5 rounded-sm">
                        <CheckCircle2 className="h-3 w-3" /> SOLD
                      </span>
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading font-bold text-[15px] text-slate-900 truncate leading-tight">
                        {listing.make} {listing.model}
                      </h3>
                      <div className="flex items-center justify-between mt-1.5 text-xs text-slate-500">
                        <span>{listing.calibre}</span>
                        {timeAgo && <span>{timeAgo}</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-500">
                        <MapPin className="h-3 w-3 text-slate-400" />{listing.state}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {/* Sell Yours CTA */}
            <div className="mt-8 bg-white border border-slate-200 rounded-sm p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="sell-yours-cta">
              <div className="text-center sm:text-left">
                <h3 className="font-heading text-lg font-bold text-slate-900">Got something similar? It could go just as fast.</h3>
                <p className="text-sm text-slate-600 mt-1">Verified sellers list from $9. Reach thousands of licensed buyers Australia-wide.</p>
              </div>
              <Button asChild variant="accent" size="lg" className="whitespace-nowrap shrink-0">
                <Link to="/register" data-testid="sell-yours-cta-btn">List yours now</Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Dealer band */}
      <section className="bg-slate-900 text-white" data-testid="dealer-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400 mb-3">For dealers</p>
            <h2 className="font-heading text-3xl md:text-[34px] font-bold tracking-tight leading-[1.1] mb-3">
              Put your whole shop in front of licensed buyers.
            </h2>
            <p className="text-slate-300 leading-relaxed max-w-xl">
              Sync your product feed once and every item stays listed, priced and photographed automatically.
              Buyers see your licence number and verified status on every ad.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row lg:justify-end gap-3">
            <Button asChild variant="accent" size="lg">
              <Link to="/dealer-onboarding" data-testid="dealer-band-register">Register as a dealer</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:border-slate-500">
              <Link to="/dealers" data-testid="dealer-band-directory">See the dealer directory</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Clubs Near You */}
      <ClubsNearYou />
      <UpcomingEvents />

      {/* Newsletter Signup */}
      <NewsletterPanel />
    </div>
  );
}

function ClubsNearYou() {
  const [postcode, setPostcode] = useState(() => localStorage.getItem("gunbuy_postcode") || "");
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Auto-search on mount if we have a saved postcode
  useEffect(() => {
    const saved = localStorage.getItem("gunbuy_postcode");
    if (saved && /^\d{4}$/.test(saved)) {
      fetchClubs(saved);
    }
  }, []);

  const fetchClubs = async (pc) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clubs?near_postcode=${pc}`);
      const data = Array.isArray(res.data) ? res.data : res.data.clubs || [];
      setClubs(data.slice(0, 3));
      setSearched(true);
      localStorage.setItem("gunbuy_postcode", pc);
    } catch {
      setClubs([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const pc = postcode.trim();
    if (/^\d{4}$/.test(pc)) fetchClubs(pc);
  };

  return (
    <section className="bg-white border-t border-slate-200 py-12" data-testid="clubs-near-you-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 mb-2">
              <Target className="h-4 w-4" /> Find your range
            </p>
            <h2 className="font-heading text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight leading-none">
              Shooting clubs near you
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="flex gap-2 shrink-0" data-testid="clubs-postcode-form">
            <Input
              value={postcode}
              onChange={e => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Your postcode"
              aria-label="Your postcode"
              className="w-36 h-11 text-sm"
              data-testid="clubs-postcode-input"
              inputMode="numeric"
              maxLength={4}
            />
            <Button
              type="submit"
              disabled={loading || !/^\d{4}$/.test(postcode.trim())}
              className="h-11 px-5"
              data-testid="clubs-postcode-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MapPin className="h-4 w-4" />Find clubs</>}
            </Button>
          </form>
        </div>

        {searched && clubs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {clubs.map(club => (
              <Link
                key={club.club_id}
                to={`/club/${club.club_id}`}
                className="group bg-white border border-slate-200 rounded-sm p-5 hover:border-slate-400 transition-colors"
                data-testid={`nearby-club-${club.club_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center shrink-0 overflow-hidden text-slate-500">
                    {club.logo_url ? (
                      <img src={club.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${club.logo_url}` : club.logo_url} alt={`${club.name} logo`} className="w-full h-full object-contain" />
                    ) : (
                      <Target className="h-6 w-6" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-bold text-slate-900 group-hover:text-orange-600 transition-colors truncate">
                      {club.name}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {club.suburb}{club.suburb && club.state ? ", " : ""}{club.state}
                    </p>
                  </div>
                  {club.distance_km != null && (
                    <Badge variant="outline" className="shrink-0 whitespace-nowrap num">
                      {club.distance_km < 1 ? "< 1 km" : `${club.distance_km.toFixed(1)} km`}
                    </Badge>
                  )}
                </div>
                {club.disciplines?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {club.disciplines.slice(0, 4).map(d => (
                      <span key={d} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">{d}</span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}

        {searched && clubs.length === 0 && !loading && (
          <div className="text-center py-6 mb-4">
            <p className="text-sm text-slate-500">No clubs found near that postcode. Try a different one or browse the full directory.</p>
          </div>
        )}

        {!searched && (
          <div className="py-4 mb-2">
            <p className="text-sm text-slate-500">Enter your postcode above to find shooting clubs and ranges in your area.</p>
          </div>
        )}

        <div>
          <Link
            to={postcode.trim().length === 4 ? `/shooting-clubs?postcode=${postcode.trim()}` : "/shooting-clubs"}
            className="text-sm font-medium text-slate-700 hover:text-orange-600 inline-flex items-center gap-1.5"
            data-testid="view-all-clubs-link"
          >
            Browse all shooting clubs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/events?limit=4`).then(r => setEvents(r.data.events || [])).catch(() => {});
  }, []);
  if (events.length === 0) return null;
  return (
    <section className="bg-slate-50 border-t border-slate-200 py-12" data-testid="upcoming-events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 mb-2">
              <CalendarDays className="h-4 w-4" /> On the calendar
            </p>
            <h2 className="font-heading text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight leading-none">
              Upcoming shooting events
            </h2>
          </div>
          <Link to="/events" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-orange-600 transition-colors" data-testid="home-all-events-link">
            All events <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {events.map(ev => <EventCard key={ev.slug} event={ev} compact />)}
        </div>
      </div>
    </section>
  );
}


function NewsletterPanel() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), first_name: firstName.trim() });
      setSubscribed(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-slate-50 border-t border-slate-200" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {subscribed ? (
          <div className="text-center" data-testid="newsletter-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-700 mx-auto mb-3" />
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">You're in</h3>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              Thanks for subscribing. We'll keep you posted on new listings, dealer updates and platform news. No spam, ever.
            </p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
            <div className="flex-1">
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 mb-2">
                <Mail className="h-4 w-4" /> Newsletter
              </p>
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2 leading-tight">
                Stay in the loop
              </h3>
              <p className="text-sm text-slate-600 max-w-md">
                Get notified about new listings, price drops, dealer spotlights and platform updates. Straight to your inbox, no spam.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex-1 w-full max-w-lg">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  aria-label="First name"
                  className="h-11 sm:w-44"
                  data-testid="newsletter-name-input"
                />
                <Input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(""); }}
                  placeholder="Your email address"
                  aria-label="Email address"
                  required
                  className="h-11 flex-1"
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-11 px-6 whitespace-nowrap shrink-0"
                  data-testid="newsletter-subscribe-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
                </Button>
              </div>
              {error && <p className="text-xs text-red-600 mt-2" data-testid="newsletter-error">{error}</p>}
              <p className="text-[11px] text-slate-500 mt-3">
                Unsubscribe any time. We respect your privacy.
              </p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
